/**
 * PhysicalStep - Fun quiz-style physical attributes
 */

import React from "react";
import {
  Box,
  Typography,
  Stack,
  Slider,
  Grid,
} from "@mui/material";
import type { DogFormData } from "../../types";
import { BodyCondition, ActivityLevel, TreatFrequency } from "@/helpers/enums";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  form: Partial<DogFormData>;
  onChange: (updates: Partial<DogFormData>) => void;
  errors?: Record<string, string>;
}

const BODY_CONDITIONS = [
  {
    value: BodyCondition.UNDERWEIGHT,
    label: "Underweight",
    description: "Ribs easily visible",
    emoji: "🦴",
    color: "#CE7A00",
    bg: "#FFF0D6",
  },
  {
    value: BodyCondition.IDEAL,
    label: "Ideal",
    description: "Ribs easily felt",
    emoji: "✨",
    color: "#2E7D32",
    bg: "#E5FFEA",
  },
  {
    value: BodyCondition.OVERWEIGHT,
    label: "Overweight",
    description: "Ribs hard to feel",
    emoji: "🍖",
    color: "#D14C3F",
    bg: "#FFE5E2",
  },
];

const ACTIVITY_LEVELS = [
  {
    value: ActivityLevel.LOW,
    label: "Couch Potato",
    description: "0-45 min/day",
    emoji: "😴",
    color: "#CE7A00",
    bg: "#FFF0D6",
  },
  {
    value: ActivityLevel.MEDIUM,
    label: "Active",
    description: "45-90 min/day",
    emoji: "🐕",
    color: "#1A57D3",
    bg: "#E6F0FF",
  },
  {
    value: ActivityLevel.HIGH,
    label: "Athlete",
    description: "90+ min/day",
    emoji: "🏃",
    color: "#2E7D32",
    bg: "#E5FFEA",
  },
];

const TREAT_FREQUENCIES = [
  { value: TreatFrequency.NEVER, label: "Never", emoji: "🚫", description: "No treats at all", color: QUIZ.color.brown, bg: "#FFF5E8" },
  { value: TreatFrequency.OCCASIONAL, label: "Sometimes", emoji: "🍪", description: "A few per week", color: "#1A57D3", bg: "#E6F0FF" },
  { value: TreatFrequency.HEAVY_SNACKER, label: "Lots!", emoji: "🦴", description: "Daily treat lover", color: "#CE7A00", bg: "#FFF0D6" },
];

// Dog sizes image from quiz
const WEIGHT_IMAGE = "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/2791748954080.png?v=1765377915";

export function PhysicalStep({ form, onChange, errors = {} }: Props) {
  const weightKg = form.weightKg || 10;

  return (
    <Stack spacing={3}>
      {/* Weight Section with Image */}
      <Box sx={{ maxWidth: { xs: "100%", md: 500 }, mx: "auto", width: "100%" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown, mb: 1.5, textAlign: "center" }}>
          How heavy is {form.name || "your dog"}?
        </Typography>

        {/* Dog sizes image from quiz */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: { xs: 2, md: 3 } }}>
          <Box
            component="img"
            src={WEIGHT_IMAGE}
            alt="Dog sizes from small to large"
            sx={{
              maxWidth: { xs: 280, sm: 360, md: 420 },
              width: "100%",
              height: "auto",
            }}
          />
        </Box>

        {/* Weight Slider Card */}
        <Box
          sx={{
            bgcolor: QUIZ.color.warmWhite,
            borderRadius: "16px",
            p: 3,
            border: "2px solid #D7C7B4",
          }}
        >

        {/* Weight Display */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: "#1A57D3",
              color: QUIZ.color.white,
              px: 3,
              py: 1.5,
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(26, 87, 211, 0.3)",
            }}
          >
            <Typography sx={{ fontSize: 28, fontWeight: 900 }}>
              {weightKg.toFixed(1)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: QUIZ.color.brown }}>
            kg
          </Typography>
        </Box>

        <Slider
          value={weightKg}
          onChange={(_, value) => onChange({ weightKg: value as number })}
          min={1}
          max={80}
          step={0.5}
          marks={[
            { value: 1, label: "1kg" },
            { value: 20, label: "20kg" },
            { value: 40, label: "40kg" },
            { value: 60, label: "60kg" },
            { value: 80, label: "80kg" },
          ]}
          sx={{
            color: "#1A57D3",
            height: 10,
            "& .MuiSlider-thumb": {
              width: 28,
              height: 28,
              bgcolor: "#1A57D3",
              border: "4px solid #FFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              "&:hover, &.Mui-focusVisible": {
                boxShadow: "0 0 0 8px rgba(26, 87, 211, 0.2)",
              },
            },
            "& .MuiSlider-track": {
              bgcolor: "#1A57D3",
              border: "none",
            },
            "& .MuiSlider-rail": {
              bgcolor: "#D4E5FF",
              opacity: 1,
            },
            "& .MuiSlider-mark": {
              bgcolor: QUIZ.color.cream3,
              width: 4,
              height: 4,
              borderRadius: "50%",
            },
            "& .MuiSlider-markLabel": {
              fontSize: 11,
              color: QUIZ.color.brown,
              fontWeight: 600,
            },
          }}
        />
        </Box>
      </Box>

      {/* Body Condition */}
      <Box sx={{ maxWidth: { xs: "100%", md: 500 }, mx: "auto", width: "100%" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown, mb: 1.5 }}>
          What's their body condition?
        </Typography>
        {errors.bodyCondition && (
          <Typography sx={{ color: "#d32f2f", fontSize: 12, fontWeight: 600, mb: 1 }}>
            {errors.bodyCondition}
          </Typography>
        )}
        <Grid container spacing={1.5}>
          {BODY_CONDITIONS.map((condition) => {
            const isSelected = form.bodyCondition === condition.value;
            return (
              <Grid size={{ xs: 12, sm: 4 }} key={condition.value}>
                <Box
                  onClick={() => onChange({ bodyCondition: condition.value })}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "14px",
                    bgcolor: isSelected ? condition.bg : QUIZ.color.white,
                    border: `3px solid ${isSelected ? condition.color : QUIZ.color.cream3}`,
                    transition: "all 0.2s ease",
                    textAlign: "center",
                    py: 2,
                    px: 1,
                    "&:hover": {
                      borderColor: condition.color,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 32, mb: 0.5 }}>
                    {condition.emoji}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 14 },
                      color: isSelected ? condition.color : QUIZ.color.brown,
                      mb: 0.25,
                    }}
                  >
                    {condition.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 11 },
                      color: QUIZ.color.brown,
                      opacity: 0.7,
                    }}
                  >
                    {condition.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Activity Level */}
      <Box sx={{ maxWidth: { xs: "100%", md: 500 }, mx: "auto", width: "100%" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown, mb: 1.5 }}>
          How active is {form.name || "your dog"}?
        </Typography>
        {errors.activityLevel && (
          <Typography sx={{ color: "#d32f2f", fontSize: 12, fontWeight: 600, mb: 1 }}>
            {errors.activityLevel}
          </Typography>
        )}
        <Grid container spacing={1.5}>
          {ACTIVITY_LEVELS.map((level) => {
            const isSelected = form.activityLevel === level.value;
            return (
              <Grid size={{ xs: 12, sm: 4 }} key={level.value}>
                <Box
                  onClick={() => onChange({ activityLevel: level.value })}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "14px",
                    bgcolor: isSelected ? level.bg : QUIZ.color.white,
                    border: `3px solid ${isSelected ? level.color : QUIZ.color.cream3}`,
                    transition: "all 0.2s ease",
                    textAlign: "center",
                    py: 2,
                    px: 1,
                    "&:hover": {
                      borderColor: level.color,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 32, mb: 0.5 }}>
                    {level.emoji}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 14 },
                      color: isSelected ? level.color : QUIZ.color.brown,
                      mb: 0.25,
                    }}
                  >
                    {level.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 11 },
                      color: QUIZ.color.brown,
                      opacity: 0.7,
                    }}
                  >
                    {level.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* Treat Frequency */}
      <Box sx={{ maxWidth: { xs: "100%", md: 500 }, mx: "auto", width: "100%" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: QUIZ.color.brown, mb: 1.5 }}>
          How often does {form.name || "your dog"} get treats?
        </Typography>
        <Grid container spacing={1.5}>
          {TREAT_FREQUENCIES.map((freq) => {
            const isSelected = form.treatFrequency === freq.value;
            return (
              <Grid size={{ xs: 12, sm: 4 }} key={freq.value}>
                <Box
                  onClick={() => onChange({ treatFrequency: freq.value })}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "14px",
                    bgcolor: isSelected ? freq.bg : QUIZ.color.white,
                    border: `3px solid ${isSelected ? freq.color : QUIZ.color.cream3}`,
                    transition: "all 0.2s ease",
                    textAlign: "center",
                    py: 2,
                    px: 1,
                    "&:hover": {
                      borderColor: freq.color,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <Typography sx={{ fontSize: 32, mb: 0.5 }}>
                    {freq.emoji}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: 12, sm: 14 },
                      color: isSelected ? freq.color : QUIZ.color.brown,
                      mb: 0.25,
                    }}
                  >
                    {freq.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: 9, sm: 11 },
                      color: QUIZ.color.brown,
                      opacity: 0.7,
                    }}
                  >
                    {freq.description}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Stack>
  );
}
